/**
 * Web-specific replacement for @react-navigation/elements/lib/module/useFrameSize.js
 * The original file uses require() which doesn't work in webpack ES module context
 * This version uses proper ES6 imports
 */

import * as React from 'react';
import { Platform, StyleSheet } from 'react-native';
import { useSafeAreaFrame } from 'react-native-safe-area-context';
import useLatestCallback from 'use-latest-callback';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/with-selector';

// On web, we don't need SafeAreaListener - just use window dimensions
const SafeAreaListener = null;

const FrameContext = React.createContext(undefined);

export function useFrameSize(selector, throttle) {
  const context = React.useContext(FrameContext);
  if (context == null) {
    throw new Error('useFrameSize must be used within a FrameSizeProvider');
  }
  const value = useSyncExternalStoreWithSelector(
    throttle ? context.subscribeThrottled : context.subscribe,
    context.getCurrent,
    context.getCurrent,
    selector
  );
  return value;
}

export function FrameSizeProvider({ initialFrame, children }) {
  const context = React.useContext(FrameContext);

  if (context != null) {
    // If the context is already present, don't wrap again
    return children;
  }

  const frame = useSafeAreaFrame();
  const frameRef = React.useRef(initialFrame ?? frame);
  const subscribersRef = React.useRef(new Set());
  const throttledSubscribersRef = React.useRef(new Set());
  const timeoutRef = React.useRef(undefined);

  const getCurrent = React.useCallback(() => frameRef.current, []);

  const subscribe = React.useCallback(callback => {
    subscribersRef.current.add(callback);
    return () => {
      subscribersRef.current.delete(callback);
    };
  }, []);

  const subscribeThrottled = React.useCallback(callback => {
    throttledSubscribersRef.current.add(callback);
    return () => {
      throttledSubscribersRef.current.delete(callback);
    };
  }, []);

  const notify = useLatestCallback(() => {
    subscribersRef.current.forEach(callback => callback());
  });

  const notifyThrottled = useLatestCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      throttledSubscribersRef.current.forEach(callback => callback());
    }, 100);
  });

  React.useEffect(() => {
    frameRef.current = frame;
    notify();
    notifyThrottled();
  }, [frame, notify, notifyThrottled]);

  const contextValue = React.useMemo(
    () => ({
      subscribe,
      subscribeThrottled,
      getCurrent,
    }),
    [subscribe, subscribeThrottled, getCurrent]
  );

  return React.createElement(FrameContext.Provider, { value: contextValue }, children);
}
